import React from 'react';
import { PLAYLIST_NAME_LIMIT, PLAYLIST_DESCRIPTION_LIMIT } from './utils/playlistValidation';

interface PlaylistFormFieldsProps {
    name: string;
    description: string;
    isPublic: boolean;
    onNameChange: (name: string) => void;
    onDescriptionChange: (description: string) => void;
    onPublicToggle: (isPublic: boolean) => void;
    nameError?: string | null;
    descriptionError?: string | null;
    disabled?: boolean;
    isEditMode?: boolean;
}

const PlaylistFormFields: React.FC<PlaylistFormFieldsProps> = ({
    name,
    description,
    isPublic,
    onNameChange,
    onDescriptionChange,
    onPublicToggle,
    nameError,
    descriptionError,
    disabled = false,
    isEditMode = false
}) => {
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onNameChange(e.target.value);
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onDescriptionChange(e.target.value);
    };

    const handlePublicToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        onPublicToggle(e.target.checked);
    };

    if (isEditMode) {
        return (
            <div className="edit-playlist-desc">
                <div className="playlist-desc-text">
                    <div className="playlist-name-container">
                        <input
                            type="text"
                            className={`edit-playlist-name ${nameError ? 'error' : ''}`}
                            value={name}
                            onChange={handleNameChange}
                            placeholder="Playlist Name"
                            maxLength={PLAYLIST_NAME_LIMIT}
                            disabled={disabled}
                        />
                        <div className="character-count">
                            {name.length}/{PLAYLIST_NAME_LIMIT}
                        </div>
                        {nameError && <div className="error-message">{nameError}</div>}
                    </div>

                    <div className="playlist-description-container">
                        <textarea
                            className={`edit-playlist-description ${descriptionError ? 'error' : ''}`}
                            value={description}
                            onChange={handleDescriptionChange}
                            placeholder="Description (optional)"
                            maxLength={PLAYLIST_DESCRIPTION_LIMIT}
                            disabled={disabled}
                        />
                        <div className="character-count">
                            {description.length}/{PLAYLIST_DESCRIPTION_LIMIT}
                        </div>
                        {descriptionError && <div className="error-message">{descriptionError}</div>}
                    </div>

                    <div className="visibility-toggle">
                        <label>
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={handlePublicToggle}
                                disabled={disabled}
                            />
                            Make this playlist public
                        </label>
                    </div>
                </div>
            </div>
        );
    }

    // Create mode
    return (
        <div className="playlist-form">
            <div className="form-group">
                <label htmlFor="playlist-name">Playlist Name</label>
                <input
                    id="playlist-name"
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    placeholder="Enter playlist name"
                    maxLength={PLAYLIST_NAME_LIMIT}
                    disabled={disabled}
                    required
                    className={nameError ? 'error' : ''}
                />
                <div className="character-count">
                    {name.length}/{PLAYLIST_NAME_LIMIT}
                </div>
                {nameError && <div className="error-message">{nameError}</div>}
            </div>

            <div className="form-group">
                <label htmlFor="playlist-description">Description (optional)</label>
                <textarea
                    id="playlist-description"
                    value={description}
                    onChange={handleDescriptionChange}
                    placeholder="Enter a description for your playlist"
                    rows={3}
                    maxLength={PLAYLIST_DESCRIPTION_LIMIT}
                    disabled={disabled}
                    className={descriptionError ? 'error' : ''}
                />
                <div className="character-count">
                    {description.length}/{PLAYLIST_DESCRIPTION_LIMIT}
                </div>
                {descriptionError && <div className="error-message">{descriptionError}</div>}
            </div>

            <div className="form-group checkbox-group">
                <label htmlFor="playlist-public">
                    <input
                        id="playlist-public"
                        type="checkbox"
                        checked={isPublic}
                        onChange={handlePublicToggle}
                        disabled={disabled}
                    />
                    Make this playlist public
                </label>
            </div>
        </div>
    );
};

export default PlaylistFormFields; 